// Lib
import Core from 'handsontable/core';
import { sum } from 'lodash';

// Utils
import { isLocationInbox, isLocationTrash } from '../../../../common/elements/utils/elementLocationUtils';
import { getContainerWidth, getTableDOMElementById, getTableScrollableElement } from './tableDOMUtils';
import { length, toArray } from '../../../../common/utils/immutableHelper';
import { getMaxFromSelections } from './tableCellSelectionUtils';
import {
    getFinalColumnIndex,
    getFinalRowIndex,
    getRowCount,
    lastColWithContent,
    lastRowWithContent,
} from './tableDataUtils';
import { parseCssPx } from '../../../utils/cssUtil';

// Types
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { TableColWidthsGU, TableContentData } from '../../../../common/table/TableTypes';

// Constants
import {
    TABLE_DEFAULT_COL_WIDTH,
    TABLE_DEFAULT_ROW_HEIGHT,
    TABLE_INITIAL_COL_COUNT,
    TABLE_MIN_ROW_COUNT,
} from '../../../../common/table/tableConstants';
import { IN_LIST_MODE } from '../../../../common/columns/columnConstants';

/**************************
 * TABLE COLUMN SIZING
 **************************/

export const getColWidthsResizeWidth = (
    element: MNElement,
    inList: string | null,
    hotTableContainerRef: React.MutableRefObject<HTMLDivElement | null>,
    getContextZoomScale?: () => number,
) => {
    const inElementList =
        isLocationInbox(element) || isLocationTrash(element) || inList === IN_LIST_MODE.IN_LIST_DOCUMENT_MODE;

    if (!inElementList) return null;

    const resizeToWidth =
        getContainerWidth(hotTableContainerRef?.current) ||
        getTableScrollableElement(hotTableContainerRef.current)?.scrollWidth;

    if (!resizeToWidth) return null;

    const zoomScale = getContextZoomScale ? getContextZoomScale() : 1;
    return resizeToWidth / zoomScale;
};

/**
 * Get a list of column widths in Px format. Account for the borders and resizing within lists.
 */
export const convertColWidthsGUtoPx = (
    colWidthsGU: Array<number>,
    gridSize: number,
    resizeToWidth: number | null = null,
): Array<number> => {
    // values for resizing into column/quicknotes etc
    const sumOfColsGu = sum(colWidthsGU);
    const sumOfColsPx = sumOfColsGu * gridSize;

    // We will resize a table up to this many px units wider than the container width
    const resizeThresholdGu = 10;
    const resizeThresholdPx = resizeThresholdGu * gridSize;

    // in a list container and table is small enough to be resized
    const shouldResizeToFit = resizeToWidth && sumOfColsPx < resizeToWidth + resizeThresholdPx;
    const multiplier = shouldResizeToFit && resizeToWidth ? resizeToWidth / sumOfColsGu : gridSize;

    return colWidthsGU.map((colWidthGU, i) => {
        let width = colWidthGU * multiplier;

        // Due to the way Handsontable distribute width to each column, which is to render the first column
        // 1px smaller, we need to do a bit of customisation in order to make sure that each column will remain
        // the same size regardless of their index in the table.
        if (i !== 0 && !shouldResizeToFit) width -= 1;

        return width;
    });
};

/**
 * Resize selected columns given a dif in px, accounting for the min column width
 */
export const getNewColumnWidthsPx = (
    selectedCols: number[],
    initialColWidths: number[],
    minColWidth: number,
    dif: number,
) => {
    const resizingColCount = selectedCols.length;
    const difPerCol = dif / resizingColCount;
    const newColWidths = initialColWidths.map((initial: number) => Math.max(initial + difPerCol, minColWidth));

    // if dif is positive, no need to calculate min widths
    if (dif > 0) return newColWidths;

    // Calculate the extra width that still needs to be divided between the columns that aren't already at min width
    let overflow = sum(newColWidths) - sum(initialColWidths) - dif;
    if (overflow === 0) return newColWidths;

    // Keep looping until there is no overflow
    // Ideally we would keep going until overflow is 0 but this way we can avoid too much looping, and it's not a visible difference
    while (overflow > 0.5) {
        // how many col widths are more than the min
        const colsAboveMin = newColWidths.filter((w) => w > minColWidth).length;

        // If all cols are at minimum, return the current newColWidths
        if (colsAboveMin === 0) return newColWidths;

        const removePerCol = overflow / colsAboveMin;

        for (let i = 0; i < resizingColCount; i++) {
            // We don't want to edit the width of a column that is already at the min
            if (newColWidths[i] === minColWidth) continue;

            // current new width, minus the additional width per col
            const newWidth: number = newColWidths[i] - removePerCol;

            // If the new width is less than the min, add the difference to the overflow and set the width to the min
            if (newWidth < minColWidth) {
                overflow += minColWidth - newWidth;
                newColWidths[i] = minColWidth;
                continue;
            }
            // If the new width is more than the min, subtract the additional width from the overflow and set the width
            overflow -= removePerCol;
            newColWidths[i] = newWidth;
        }
    }

    return newColWidths;
};

/**************************
 * GET TABLE SIZES
 **************************/

export const getHotTableTotalWidthGU = (colWidthsGU: unknown, gridSize: number): number => {
    const sumOfWidthsGU = sum(toArray(colWidthsGU));
    // Adjust for borders
    const adjustForBorders = (length(colWidthsGU) - TABLE_INITIAL_COL_COUNT) / gridSize;
    return sumOfWidthsGU - adjustForBorders;
};

export const getTotalTableHeightGU = (
    elementId: string,
    gridSize: number,
    getContextZoomScale: () => number,
): number => {
    const tableElement = getTableDOMElementById(elementId);
    if (!tableElement) return 0;

    const tableElementHeight = tableElement.getBoundingClientRect().height;
    const tableHeightGU = tableElementHeight / gridSize;
    const zoomScale = getContextZoomScale();
    return tableHeightGU / zoomScale;
};

export const getBorderRenderedValues = (
    tdElement: HTMLTableCellElement | null,
): { actualBorderWidth: number; missingBorderWidth: number } => {
    if (!tdElement) return { actualBorderWidth: 1, missingBorderWidth: 0 };

    // These values are to account for 1px borders being rendered differently at different browser zoom levels
    const actualBorderWidth = parseCssPx(getComputedStyle(tdElement).borderBottomWidth) || 1;
    const missingBorderWidth = 1 - actualBorderWidth;

    return { actualBorderWidth, missingBorderWidth };
};

/**************************
 * TABLE ROW HEIGHTS
 **************************/

export const updateRowHeights = (hotInstance: Core): void => {
    if (!hotInstance) return;

    // @ts-ignore - valid custom property
    const { gridSize, showTitle, showCaption } = hotInstance.milanoteProps;

    const tdElement = hotInstance.rootElement.querySelector('td');
    const { missingBorderWidth } = getBorderRenderedValues(tdElement);

    // important to do this using the manual row heights plugin so that we can get partial px values
    const manualRowResizePluginInstance = hotInstance.getPlugin('manualRowResize');

    for (let rowIndex = 0; rowIndex < hotInstance.countRows(); rowIndex++) {
        // First and last rows are a bit shorter so that they fit within the grid points, 1px for the border
        // and 0.5px so that the corner sits in the middle of the grid point
        const shorterRow = (rowIndex === 0 && !showTitle) || (rowIndex === hotInstance.countRows() - 1 && !showCaption);

        const standardHeight = TABLE_DEFAULT_ROW_HEIGHT * gridSize + missingBorderWidth;
        const newHeight = shorterRow ? standardHeight - 1.5 : standardHeight;

        manualRowResizePluginInstance.setManualSize(rowIndex, newHeight);
    }
};

/**************************
 * UPDATE TABLE SIZE
 **************************/

/**
 * This function makes the changes to the data when using the drag handle to
 * resize the table.
 * It takes the difference between the current and temp size, uses that to calculate
 * how many rows/columns to add or delete, then makes the changes to the data and
 * returns that updated data.
 * It will only delete empty cells.
 */
export const resizeTable = (
    elementId: string,
    data: TableContentData,
    colWidthsGU: TableColWidthsGU,
    tempSize: { width: number; height: number },
    gridSize: number,
    hotInstance: Core,
    getContextZoomScale: () => number,
): { columnDif: number; rowDif: number } => {
    const { width: tempWidth, height: tempHeight } = tempSize;

    // Get the difference between table and tempSize in grid units
    const tableWidth = sum(colWidthsGU);
    const widthDif = tempWidth - tableWidth;
    const tableHeight = getTotalTableHeightGU(elementId, gridSize, getContextZoomScale);
    const heightDif = tempHeight - tableHeight;

    // Allow the cursor to be a bit closer to the table for something to change
    // e.g. the cursor can be half a col width away to trigger adding a new column
    const widthAdjustment = TABLE_DEFAULT_COL_WIDTH / 2;
    const heightAdjustment = TABLE_DEFAULT_ROW_HEIGHT / 2;

    // positive or negative number that describes how many cols/rows will be added or removed
    let columnDif = Math.floor((widthDif + widthAdjustment) / TABLE_DEFAULT_COL_WIDTH);
    let rowDif = Math.floor((heightDif + heightAdjustment) / TABLE_DEFAULT_ROW_HEIGHT);

    // If either dif is negative, we need to check for any cells that shouldn't be deleted and update the value if necessary
    if (columnDif < 0) {
        const finalColIndex = getFinalColumnIndex(data);
        const proposedFinalIndex = finalColIndex + columnDif;

        // get the last column with content or selection
        const maxColWithContent = lastColWithContent(hotInstance, data) || 0;
        const maxSelectedCol = getMaxFromSelections(hotInstance.getSelected())?.col || 0;
        const maxColWithContentOrSelection = Math.max(maxColWithContent, maxSelectedCol);

        // if the proposed final index is less than the max row with content or selection, update the rowDif
        if (proposedFinalIndex < maxColWithContentOrSelection) columnDif = maxColWithContentOrSelection - finalColIndex;
    }
    if (rowDif < 0) {
        const finalRowIndex = getFinalRowIndex(data);
        const proposedFinalIndex = finalRowIndex + rowDif;

        // get the last row with content or selection
        const maxRowWithContent = lastRowWithContent(hotInstance, data) || 0;
        const maxSelectedRow = getMaxFromSelections(hotInstance.getSelected())?.row || 0;
        const maxRowWithContentOrSelection = Math.max(maxRowWithContent, maxSelectedRow);

        // if the proposed final index is less than the max row with content or selection, update the rowDif
        if (proposedFinalIndex < maxRowWithContentOrSelection) rowDif = maxRowWithContentOrSelection - finalRowIndex;

        // if proposed row count is less than the min row count, update the rowDif
        const currentRowCount = getRowCount(data);
        if (currentRowCount + rowDif < TABLE_MIN_ROW_COUNT) {
            rowDif = TABLE_MIN_ROW_COUNT - currentRowCount;
        }
    }

    return { columnDif, rowDif };
};
